/**
 * 서버 설치 매뉴얼 — 서버 설치 파일 메뉴의 두 번째 탭.
 *
 * 받는 사람이 설치를 하다 막히는 곳은 거의 늘 **자기 권한 밖의 일**이다 — 계정, USB 연결, 전용 Docker 준비,
 * 방화벽. 설치기는 그 명령을 보여 주고 멈추지만, 그것은 이미 고객사에 가 있을 때다. 그래서 이 화면은 둘을 준다.
 *
 * 1. **관리자(sudo) 명령 만들기** — 사용자 이름·설치 위치·포트를 넣으면 이 서버용 명령을 만든다. 방문 전에
 *    관리자에게 보내 둔다. 명령은 설치기가 보여 주는 것과 같다(`serverInstallManual.ts` 의 시험이 대조한다).
 * 2. **매뉴얼 본문** — zip 안의 `INSTALL-GUIDE.md` 와 같은 원본(`deploy/package/guide-*.md`)을 그대로 그린다.
 *    콘솔에서 읽은 것과 USB 에서 읽은 것이 다르면 사람은 어느 쪽도 믿지 않는다.
 * 3. **PDF 로 내려받기** — 설치 기사는 고객사에서 인터넷도 이 콘솔도 없이 움직인다. 종이로 뽑거나 노트북에
 *    담아 갈 것이 필요하다. 만드는 소스는 화면에 그린 것과 **같은 문자열**이라 둘이 갈라지지 않고,
 *    표지에 이 콘솔이 아는 패키지 판이 찍힌다 — USB 폴더 이름과 맞춰 볼 수 있어야 한다.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SignageOneClient } from '../api/client.js';
import { Markdown, outlineOf } from '../components/markdown.js';
import { CommandBox, ErrorNote, Loading, Panel } from '../components/ui.js';
import { useAsync } from '../hooks.js';
import { HelpTip } from '../components/HelpTip.js';
import {
  adminScript,
  adminSteps,
  defaultPrefix,
  fillGuide,
  guideValues,
  installGuideFilename,
  loadGuide,
  validateAdminInput,
  VARIANT_TITLE,
  type AdminInput,
  type DockerMode,
  type ManualVariant,
  type PackageManager,
} from '../serverInstallManual.js';
import { ServerPackagesTabs } from './ServerPackages.js';

export function ServerPackageManualScreen({ client }: { client: SignageOneClient }): ReactNode {
  const [params, setParams] = useSearchParams();
  // 주소에 남긴다 — "네이티브 판 매뉴얼" 을 링크로 건넬 수 있게.
  const variant: ManualVariant = params.get('variant') === 'native' ? 'native' : 'docker';
  const setVariant = (next: ManualVariant): void => {
    const copy = new URLSearchParams(params);
    copy.set('variant', next);
    setParams(copy, { replace: true });
  };

  const packages = useAsync(() => client.listServerPackages(), [client]);
  const guide = useAsync(() => loadGuide(variant), [variant]);

  // 올라와 있는 최신 판으로 판·폴더 이름을 채운다. 목록을 못 읽어도 매뉴얼은 그린다.
  const latest = packages.data?.items.find(
    (item) => item.variant === variant && item.arch === 'x64' && item.status === 'ready',
  );
  const source = useMemo(
    () => (guide.data ? fillGuide(guide.data, guideValues({ variant, version: latest?.version ?? null })) : ''),
    [guide.data, variant, latest?.version],
  );
  const outline = useMemo(() => (source ? outlineOf(source) : []), [source]);

  return (
    <div className="screen animate-fade-in">
      <ServerPackagesTabs active="manual" />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] text-slate-500">매뉴얼</span>
        {(['docker', 'native'] as const).map((option) => (
          <button
            key={option}
            type="button"
            className={`btn btn-tiny ${variant === option ? 'btn-primary' : ''}`}
            aria-pressed={variant === option}
            onClick={() => setVariant(option)}
          >
            {VARIANT_TITLE[option]}
          </button>
        ))}
        <span className="hint">
          {latest ? `판 ${latest.version} 기준` : '등록된 설치 파일이 없어 판 번호 없이 표시합니다'}
        </span>
        <span className="grow" />
        <GuidePdfButton source={source} variant={variant} version={latest?.version ?? null} />
      </div>

      <AdminCommandPanel key={variant} variant={variant} />

      <div className="flex gap-6">
        <article className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-5 sm:px-7 sm:py-6">
          {guide.loading && !guide.data ? <Loading /> : null}
          {guide.error ? <ErrorNote message={`매뉴얼을 불러오지 못했습니다: ${guide.error}`} /> : null}
          {source ? <Markdown source={source} copyCode /> : null}
        </article>

        {/* 목차는 넓은 화면에서만 — 좁은 화면에서 본문 위에 얹으면 정작 읽을 것을 가린다(시스템 문서와 같은 규칙). */}
        {outline.length > 1 ? (
          <aside className="hidden w-56 shrink-0 xl:block">
            <div className="sticky top-6">
              <p className="mb-2 px-3 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">목차</p>
              <ul className="max-h-[calc(100vh-8rem)] space-y-0.5 overflow-y-auto">
                {outline.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-1.5 text-left text-[12.5px] leading-snug text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                      // 해시 라우팅이라 #앵커 주소를 쓰면 라우트를 덮어쓴다. 스크롤로 옮긴다.
                      onClick={() => document.getElementById(item.id)?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                    >
                      {item.text}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}

/**
 * 관리자(sudo) 명령 만들기.
 *
 * 설치 위치는 사용자 이름을 따라 바뀐다 — 사람이 직접 고친 뒤에는 따라가지 않는다. AppArmor 프로필은 설치 위치로
 * 이름이 정해져, 위치가 하나라도 어긋나면 설치기가 같은 작업을 또 요구한다.
 */
function AdminCommandPanel({ variant }: { variant: ManualVariant }): ReactNode {
  const [user, setUser] = useState('signage');
  const [prefix, setPrefix] = useState<string | null>(null);
  const [port, setPort] = useState('4600');
  const [packageManager, setPackageManager] = useState<PackageManager>('apt');
  const [dockerMode, setDockerMode] = useState<DockerMode>('rootless');

  const input: AdminInput = {
    variant,
    user: user.trim(),
    prefix: (prefix ?? defaultPrefix(user.trim())).trim(),
    port: Number(port),
    packageManager,
    dockerMode,
  };
  const errors = validateAdminInput(input);
  const steps = errors.length === 0 ? adminSteps(input) : [];

  return (
    <Panel
      title="관리자(sudo) 명령 만들기"
      subtitle="방문 전에 서버 관리자에게 보낼 사전 작업 명령"
      collapsible
    >
      <div className="space-y-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="field">
            <span className="field-label">설치할 사용자</span>
            <input className="input" value={user} onChange={(event) => setUser(event.target.value)} spellCheck={false} />
          </label>
          <label className="field">
            <span className="field-label">
              설치 위치
              <HelpTip
                text="기본값은 그 사용자의 $HOME/signageone 입니다. 홈이 /home/사용자 가 아니면 그 사용자로 echo $HOME 을 확인해 고치세요."
                label="설치 위치 도움말"
              />
            </span>
            <input
              className="input mono"
              value={prefix ?? defaultPrefix(user.trim())}
              onChange={(event) => setPrefix(event.target.value)}
              spellCheck={false}
            />
          </label>
          <label className="field">
            <span className="field-label">접속 포트</span>
            <input className="input" inputMode="numeric" value={port} onChange={(event) => setPort(event.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">운영체제 계열</span>
            <select
              className="input"
              value={packageManager}
              onChange={(event) => setPackageManager(event.target.value as PackageManager)}
            >
              <option value="apt">Ubuntu · Debian</option>
              <option value="dnf">RHEL · Rocky · Alma</option>
            </select>
          </label>
        </div>

        {variant === 'docker' ? (
          <fieldset className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-slate-700">
            <legend className="sr-only">Docker 엔진</legend>
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="docker-mode"
                checked={dockerMode === 'rootless'}
                onChange={() => setDockerMode('rootless')}
              />
              전용 Docker 설치 (rootless)
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="radio" name="docker-mode" checked={dockerMode === 'system'} onChange={() => setDockerMode('system')} />
              기존 Docker 사용 (docker 그룹)
            </label>
            <HelpTip
              text="서버에 쓸 수 있는 Docker 가 없으면 사용자 전용 Docker(rootless)를 설치합니다. 이미 있으면 사용자를 docker 그룹에 넣어 그것을 씁니다."
              label="Docker 엔진 도움말"
            />
          </fieldset>
        ) : null}

        {errors.length > 0 ? (
          <ul className="list-disc space-y-1 pl-5 text-[12.5px] text-red-700">
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        ) : (
          <>
            <ol className="space-y-3">
              {steps.map((step, at) => (
                <li key={step.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-[12px] font-semibold text-slate-400">{at + 1}</span>
                    <h3 className="text-[13.5px] font-semibold text-slate-800">{step.title}</h3>
                  </div>
                  <p className="mt-1 text-[12.5px] text-slate-600">
                    <span className="font-medium text-slate-700">언제:</span> {step.when}
                  </p>
                  {step.check ? (
                    <p className="mt-0.5 text-[12.5px] text-slate-600">
                      <span className="font-medium text-slate-700">확인:</span>{' '}
                      <code className="mono break-all">{step.check}</code>
                    </p>
                  ) : null}
                  <div className="mt-2">
                    <CommandBox command={step.commands} />
                  </div>
                  {step.note ? <p className="hint mt-1.5">{step.note}</p> : null}
                </li>
              ))}
            </ol>

            <details className="rounded-lg border border-slate-200">
              <summary className="cursor-pointer px-3 py-2 text-[13px] font-medium text-slate-700">
                관리자에게 보낼 전체 명령
              </summary>
              <div className="p-3 pt-0">
                <CommandBox command={adminScript(input, steps)} />
              </div>
            </details>
          </>
        )}
      </div>
    </Panel>
  );
}

/**
 * 매뉴얼을 PDF 로 내려받는다.
 *
 * **누를 때 만든다.** 미리 구워 두면(사용 설명서가 그렇다) 표지에 이 콘솔이 아는 패키지 판을 적을 수
 * 없다 — 그 값은 서버마다 다르다. 대신 생성기를 **그때 받는다**(pdf-lib·한글 글꼴 약 730KB): 이 화면은
 * 공급사만 여는 자리라 콘솔 첫 화면의 무게에는 영향이 없다.
 */
function GuidePdfButton({
  source,
  variant,
  version,
}: {
  source: string;
  variant: ManualVariant;
  version: string | null;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const { buildInstallGuidePdf } = await import('../manual/export/pdf.js');
      const bytes = await buildInstallGuidePdf(source, {
        variant: VARIANT_TITLE[variant],
        version: version ?? '콘솔에 올라온 최신 판',
        arch: 'x86_64 (amd64)',
      });
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = installGuideFilename(variant, version);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }, [source, variant, version]);

  return (
    <span className="flex items-center gap-2">
      {error ? <span className="text-[12px] text-rose-600">PDF 를 만들지 못했습니다: {error}</span> : null}
      <button
        type="button"
        className="btn btn-tiny"
        disabled={busy || source === ''}
        onClick={() => void download()}
        title="현장에 가져갈 수 있게 이 매뉴얼을 PDF 로 받습니다"
      >
        {busy ? 'PDF 만드는 중…' : 'PDF 내려받기'}
      </button>
    </span>
  );
}
